import {WidgetRendererProps} from '@app/dashboard/biolink/biolink-editor/content/widgets/types/widget-renderer-props';
import {YoutubeWidget} from '@app/dashboard/biolink/biolink-editor/content/widgets/youtube-widget/youtube-widget-dialog';
import getVideoId from 'get-video-id';
import {VideoEmbedWidgetRenderer} from '@app/dashboard/biolink/biolink-editor/content/widgets/video-embed-widget-renderer';
import {useEffect, useRef} from 'react';
import {apiClient} from '@common/http/query-client';

declare global {
  interface Window {
    YT?: any;
  }
}

export function YoutubeWidgetRenderer({
  widget,
  variant,
}: WidgetRendererProps<YoutubeWidget>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (variant === 'editor') return;

    if (!window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        window.dispatchEvent(new CustomEvent('youtube-api-ready'));
      };
    }

    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }

    const initPlayer = () => {
      if (!iframeRef.current || !window.YT || !window.YT.Player) return;

      let previousState = window.YT.PlayerState.UNSTARTED;

      new window.YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event: any) => {
            const currentState = event.data;
            const PlayerState = window.YT.PlayerState;

            if (currentState === PlayerState.PLAYING) {
              if (
                previousState === PlayerState.UNSTARTED ||
                previousState === PlayerState.ENDED ||
                previousState === PlayerState.CUED
              ) {
                apiClient
                  .post('biolink/widget/playback', {
                    widget_id: widget.id,
                  })
                  .catch(() => {});
              }
            }

            if (currentState !== PlayerState.BUFFERING) {
              previousState = currentState;
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.addEventListener('youtube-api-ready', initPlayer);
      return () => window.removeEventListener('youtube-api-ready', initPlayer);
    }
  }, [variant, widget.id]);

  if (!widget.config.url) return null;

  const {id} = getVideoId(widget.config.url);
  const embedUrl = `https://www.youtube.com/embed/${id}?enablejsapi=1&origin=${window.location.origin}`;

  if (variant === 'editor') {
    return <VideoEmbedWidgetRenderer variant={variant} embedUrl={embedUrl} />;
  }

  return (
    <iframe
      ref={iframeRef}
      className="aspect-video w-full rounded shadow-lg"
      loading="lazy"
      src={embedUrl}
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
}
